"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AsuProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const sub_1 = __importDefault(require("date-fns/sub"));
const react_router_dom_1 = require("react-router-dom");
const use_debounce_1 = require("use-debounce");
const qs_1 = require("qs");
const provider_user_1 = require("@framework/provider-user");
const provider_settings_1 = require("@framework/provider-settings");
const react_hooks_1 = require("@framework/react-hooks");
const context_1 = require("./context");
const interfaces_1 = require("./interfaces");
const menu_list_1 = require("./menu-list");
const menu_lookup_1 = require("./menu-lookup");
const search_list_1 = require("./search-list");
const utils_1 = require("./utils");
const recorder_loop_1 = require("./recorder-loop");
const mergeMenuSignaling_1 = require("./recorder/mergeMenuSignaling");
const createMenuData = (rawList = []) => ({
    rawList,
    menuList: new menu_list_1.MenuList(rawList),
    lookup: (0, menu_lookup_1.buildMenuLookup)(rawList),
});
const applyStationStatusOverrides = (items, stationStatuses) => {
    var _a;
    for (const item of items) {
        applyStationStatusOverrides(item.children, stationStatuses);
        const status = stationStatuses.get(item.Id);
        if (status !== undefined) {
            item.MaxAlarmStatus = Math.max((_a = item.MaxAlarmStatus) !== null && _a !== void 0 ? _a : 0, status);
        }
    }
    return items;
};
const getRecorderModePeriodName = (range) => range === undefined ? "" : provider_user_1.SelfRecorderRangeEnum[range];
const getRecorderModeApplySettings = (settings) => {
    if (!settings) {
        return null;
    }
    return {
        range: settings.range,
        value: settings.value,
    };
};
const SETTINGS_STORAGE_NAME = "settings";
const hasStoredUserProfile = () => {
    const storedUser = window.localStorage.getItem("user");
    return Boolean(storedUser && storedUser !== "null");
};
const readUserSettingsFromLocalStorage = (userId) => {
    try {
        const data = localStorage.getItem(`${SETTINGS_STORAGE_NAME}_${userId}`);
        return data ? JSON.parse(data) : {};
    }
    catch (_a) {
        return {};
    }
};
const getPresetRangeTimestamps = (rangeType, endTime) => {
    let startTime = endTime;
    if (rangeType === interfaces_1.RangeType.WORK_DAY) {
        startTime = (0, sub_1.default)(endTime, { hours: 8 });
    }
    else if (rangeType === interfaces_1.RangeType.FULL_DAY) {
        startTime = (0, sub_1.default)(endTime, { days: 1 });
    }
    else if (rangeType === interfaces_1.RangeType.WEEK) {
        startTime = (0, sub_1.default)(endTime, { weeks: 1 });
    }
    else if (rangeType === interfaces_1.RangeType.MONTH) {
        startTime = (0, sub_1.default)(endTime, { months: 1 });
    }
    else if (rangeType === interfaces_1.RangeType.YEAR) {
        startTime = (0, sub_1.default)(endTime, { years: 1 });
    }
    else {
        startTime = (0, sub_1.default)(endTime, { hours: 8 });
    }
    return {
        start: startTime.toISOString(),
        end: endTime.toISOString(),
    };
};
const AsuProvider = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const { children } = props;
    const user = (0, provider_user_1.useUser)();
    const location = (0, react_router_dom_1.useLocation)();
    const settings = (0, provider_settings_1.useSettings)();
    const [searchParams, setSearchParams] = (0, react_router_dom_1.useSearchParams)();
    const [dbTz, setDbTz] = (0, react_1.useState)(interfaces_1.DB_TZ);
    const [isMenuOpen, setIsMenuOpen] = (0, react_1.useState)(true);
    const [isAmplitudePhaseSelected, setIsAmplitudePhaseSelected] = (0, react_1.useState)(false);
    const [isVisualControlOpen, setIsVisualControlOpen] = (0, react_1.useState)(false);
    const [shouldReloadJournal, setShouldReloadJournal] = (0, react_1.useState)(false);
    const [isJournalNavigation, setIsJournalNavigation] = (0, react_1.useState)(false);
    const [skipNextJournalReload, setSkipNextJournalReload] = (0, react_1.useState)(false);
    const [justRestoredRange, setJustRestoredRange] = (0, react_1.useState)(false);
    const [tablesActiveTab, setTablesActiveTab] = (0, react_1.useState)("1");
    const [recorderTickId, setRecorderTickId] = (0, react_1.useState)(0);
    const [recorderWindowStart, setRecorderWindowStart] = (0, react_1.useState)("");
    const [recorderWindowEnd, setRecorderWindowEnd] = (0, react_1.useState)("");
    const [recorderResetKey, setRecorderResetKey] = (0, react_1.useState)(0);
    const [appliedRecorderModeSettings, setAppliedRecorderModeSettings] = (0, react_1.useState)(null);
    const [pendingAnalyticsParams, setPendingAnalyticsParams] = (0, react_1.useState)(null);
    const selfRecorderSettings = (0, react_1.useMemo)(() => { var _a; return (_a = user === null || user === void 0 ? void 0 : user.profile) === null || _a === void 0 ? void 0 : _a.selfRecorderSettings; }, [
        (_b = (_a = user === null || user === void 0 ? void 0 : user.profile) === null || _a === void 0 ? void 0 : _a.selfRecorderSettings) === null || _b === void 0 ? void 0 : _b.value,
        (_d = (_c = user === null || user === void 0 ? void 0 : user.profile) === null || _c === void 0 ? void 0 : _c.selfRecorderSettings) === null || _d === void 0 ? void 0 : _d.range,
        (_f = (_e = user === null || user === void 0 ? void 0 : user.profile) === null || _e === void 0 ? void 0 : _e.selfRecorderSettings) === null || _f === void 0 ? void 0 : _f.upd,
    ]);
    const updateQS = (params) => {
        const currentSearchParams = (0, qs_1.parse)(location.search.replace("?", ""));
        const nextSearchParams = Object.assign({}, currentSearchParams);
        Object.keys(params).forEach(key => {
            const value = params[key];
            if (value === undefined || value === "" || value === false) {
                return;
            }
            if (value === null) {
                delete nextSearchParams[key];
                return;
            }
            nextSearchParams[key] = value;
        });
        if (params.stationId && currentSearchParams.trainId) {
            delete nextSearchParams.trainId;
        }
        if (params.trainId && currentSearchParams.stationId) {
            delete nextSearchParams.stationId;
        }
        if (currentSearchParams.query && !params.query) {
            delete nextSearchParams.query;
        }
        const startTimestamp = nextSearchParams.startTimestamp;
        const endTimestamp = nextSearchParams.endTimestamp;
        const rangeType = nextSearchParams.rangeType;
        if (startTimestamp && endTimestamp) {
            settings.setTimestamps(startTimestamp, endTimestamp);
        }
        if (rangeType) {
            settings.setRangeType(rangeType);
        }
        setSearchParams(nextSearchParams);
    };
    const savedTab = settings.getTab();
    const savedStationId = settings.getStationId();
    const savedTrainId = settings.getTrainId();
    const savedStartTimestamp = settings.getStartTimestamp();
    const savedEndTimestamp = settings.getEndTimestamp();
    const savedRangeType = settings.getRangeType();
    const [manualRangeType, setManualRangeType] = (0, react_1.useState)(null);
    const query = searchParams.get("query") || null;
    const tab = searchParams.get("tab") || savedTab || "1";
    const disabledRecorderModeTabs = ["4"];
    const isRecorderModeDisabledForTab = disabledRecorderModeTabs.includes(tab);
    const rawRangeType = manualRangeType !== null
        ? manualRangeType
        : searchParams.get("rangeType") || savedRangeType || interfaces_1.RangeType.WORK_DAY;
    const rangeType = isRecorderModeDisabledForTab && rawRangeType === interfaces_1.RangeType.RECORDER_MODE ? interfaces_1.RangeType.WORK_DAY : rawRangeType;
    const isRecorderMode = rangeType === interfaces_1.RangeType.RECORDER_MODE;
    const nowFakeUtc = (0, utils_1.toFakeUtc)(new Date(), dbTz);
    const defaultEndTimestamp = isRecorderMode
        ? nowFakeUtc.toISOString()
        : searchParams.get("endTimestamp") || savedEndTimestamp || nowFakeUtc.toISOString();
    const defaultStartTimestamp = isRecorderMode
        ? (0, utils_1.calculateStartDate)(nowFakeUtc, provider_user_1.SelfRecorderRangeEnum[(_g = selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.range) !== null && _g !== void 0 ? _g : provider_user_1.SelfRecorderRangeEnum.minutes], (_h = selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.value) !== null && _h !== void 0 ? _h : 5).toISOString()
        : searchParams.get("startTimestamp") || savedStartTimestamp || (0, sub_1.default)(nowFakeUtc, { hours: 8 }).toISOString();
    const defaultPrecision = searchParams.get("precision") || interfaces_1.Precision.hour;
    const [startTimestamp, setStartTimestamp] = (0, react_1.useState)(defaultStartTimestamp);
    const [endTimestamp, setEndTimestamp] = (0, react_1.useState)(defaultEndTimestamp);
    const [userSelectedStartTimestamp, setUserSelectedStartTimestamp] = (0, react_1.useState)(typeof startTimestamp === "string" ? new Date(startTimestamp) : startTimestamp);
    const [userSelectedEndTimestamp, setUserSelectedEndTimestamp] = (0, react_1.useState)(typeof endTimestamp === "string" ? new Date(endTimestamp) : endTimestamp);
    const [userSelectedRangeType, setUserSelectedRangeType] = (0, react_1.useState)(rangeType);
    const isRecorderModeActive = userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE && appliedRecorderModeSettings !== null;
    const [userSelectedRecorderModePeriod, setUserSelectedRecorderModePeriod] = (0, react_1.useState)(isRecorderMode ? getRecorderModePeriodName(selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.range) : "");
    const [recorderModeInterval, setRecorderModeInterval] = (0, react_1.useState)(isRecorderMode ? (_j = selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.value) !== null && _j !== void 0 ? _j : 0 : 0);
    const [updatingValueRecorderMode, setUpdatingValueRecorderMode] = (0, react_1.useState)(5);
    const [isRecorderModeDraftDirty, setIsRecorderModeDraftDirty] = (0, react_1.useState)(false);
    const [precision, setPrecision] = (0, react_1.useState)(defaultPrecision);
    const [menuData, setMenuData] = (0, react_1.useState)(() => createMenuData());
    const rawList = menuData.rawList;
    const rawListRef = (0, react_1.useRef)(rawList);
    rawListRef.current = rawList;
    const rawMenuList = menuData.menuList;
    const menuLookup = menuData.lookup;
    const [list, setList] = (0, react_1.useState)(null);
    const [isMenuLoadFailed, setIsMenuLoadFailed] = (0, react_1.useState)(false);
    const [subscribeSignalingActive, setSubscribeSignalingActive] = (0, react_1.useState)(false);
    const stationStatusOverridesRef = (0, react_1.useRef)(new Map());
    const stationAlarmHistoryRef = (0, react_1.useRef)(new Map());
    const trainAlarmHistoryRef = (0, react_1.useRef)(new Map());
    const updateListRequestVersionRef = (0, react_1.useRef)(0);
    const restoredForUserIdRef = (0, react_1.useRef)(null);
    const recorderLastTickTimeRef = (0, react_1.useRef)(null);
    const [filters, setFilters] = (0, react_1.useState)(interfaces_1.emptyFilters);
    const [isFilterApplied, setIsFilterApplied] = (0, react_1.useState)(false);
    const [filterOptions, setFilterOptions] = (0, react_1.useState)({});
    const [currentStatusFilter, setCurrentStatusFilter] = (0, react_1.useState)(interfaces_1.FilterStatus.all);
    const [selectedItem, setSelectedItem] = (0, react_1.useState)(list ? list[0] : null);
    const [journalTimeRangeHistory, setJournalTimeRangeHistory] = (0, react_1.useState)([]);
    const equipmentStateDisplayMode = (_m = (_l = (_k = user.profile) === null || _k === void 0 ? void 0 : _k.interfaceSettings) === null || _l === void 0 ? void 0 : _l.equipmentStateDisplayMode) !== null && _m !== void 0 ? _m : provider_user_1.EquipmentStateDisplayMode.GOST;
    const shouldPreserveStationStateAcrossFilters = equipmentStateDisplayMode !== provider_user_1.EquipmentStateDisplayMode.GOST;
    const getFilteredMenuList = (0, react_1.useCallback)((menuList, nextFilters, nextStatusFilter) => {
        const filtered = menuList.buildFilteredList(nextFilters, nextStatusFilter, isVisualControlOpen, shouldPreserveStationStateAcrossFilters);
        return applyStationStatusOverrides(filtered, stationStatusOverridesRef.current);
    }, [isVisualControlOpen, shouldPreserveStationStateAcrossFilters]);
    const selectedItemKey = selectedItem
        ? "Id" in selectedItem
            ? `station-${selectedItem.Id}`
            : `train-${selectedItem.TrainId}`
        : null;
    const { fn: fetchMenuTree, isLoading: isMenuTreeLoading } = (0, react_hooks_1.useApiCall)((apiContext) => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return Promise.resolve(undefined);
        }
        return apiContext.fetchJson({
            url: "/menu",
            method: "GET",
        });
    }, { success: false });
    const { fn: fetchMenuData, isLoading: isMenuDataLoading } = (0, react_hooks_1.useApiCall)((apiContext, values) => {
        var _a;
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return Promise.resolve(undefined);
        }
        return apiContext.fetchJson({
            url: "/menu-data",
            method: "GET",
            data: {
                startTimestamp: values.startTimestamp,
                endTimestamp: values.endTimestamp,
                equipmentStateDisplayMode: (_a = values.equipmentStateDisplayMode) !== null && _a !== void 0 ? _a : equipmentStateDisplayMode,
            },
        });
    }, { success: false });
    const isLoading = isMenuTreeLoading || isMenuDataLoading;
    const applySignalingUpdate = (0, react_1.useCallback)((mappedJson) => {
        if (!rawList.length) {
            return;
        }
        stationStatusOverridesRef.current.clear();
        stationAlarmHistoryRef.current.clear();
        trainAlarmHistoryRef.current.clear();
        if (userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE && recorderWindowStart) {
            menu_list_1.MenuList.mergeRecorderSignalingIntoRawList(rawList, mappedJson, recorderWindowStart, dbTz);
        }
        else {
            menu_list_1.MenuList.mergeSignalingIntoRawList(rawList, mappedJson);
        }
        const nextMenuList = new menu_list_1.MenuList(rawList);
        const nextLookup = (0, menu_lookup_1.buildMenuLookup)(rawList);
        const filtered = getFilteredMenuList(nextMenuList, filters, currentStatusFilter);
        setMenuData({
            rawList,
            menuList: nextMenuList,
            lookup: nextLookup,
        });
        setList(filtered);
        setSelectedItem(prev => {
            var _a;
            if (prev && "TrainId" in prev && prev.TrainId) {
                return (_a = nextLookup.trainsById.get(prev.TrainId)) !== null && _a !== void 0 ? _a : prev;
            }
            return prev;
        });
    }, [rawList, filters, currentStatusFilter, getFilteredMenuList, userSelectedRangeType, recorderWindowStart, dbTz]);
    const applySubscribeMenuStatusUpdates = (0, react_1.useCallback)((updates) => {
        var _a, _b, _c, _d, _e, _f;
        const currentRawList = rawListRef.current;
        if (!currentRawList.length || !updates.length) {
            return;
        }
        const recorderSettings = appliedRecorderModeSettings !== null && appliedRecorderModeSettings !== void 0 ? appliedRecorderModeSettings : selfRecorderSettings;
        const recorderRange = (_a = recorderSettings === null || recorderSettings === void 0 ? void 0 : recorderSettings.range) !== null && _a !== void 0 ? _a : provider_user_1.SelfRecorderRangeEnum.minutes;
        const recorderValue = (_b = recorderSettings === null || recorderSettings === void 0 ? void 0 : recorderSettings.value) !== null && _b !== void 0 ? _b : 5;
        const updatesByTrain = new Map();
        const updatesByStation = new Map();
        for (const update of updates) {
            const updatesByObject = update.objectType === "station" ? updatesByStation : updatesByTrain;
            const objectUpdates = (_c = updatesByObject.get(update.objectId)) !== null && _c !== void 0 ? _c : [];
            objectUpdates.push(update);
            updatesByObject.set(update.objectId, objectUpdates);
        }
        const nextRawList = currentRawList.map(station => {
            let stationChanged = false;
            const nextTrains = station.Trains.map(train => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                if (!train.TrainId) {
                    return train;
                }
                const trainUpdates = updatesByTrain.get(train.TrainId);
                if (!(trainUpdates === null || trainUpdates === void 0 ? void 0 : trainUpdates.length)) {
                    return train;
                }
                let history = trainAlarmHistoryRef.current.get(train.TrainId);
                if (!history) {
                    const currentPoint = (_a = train.Points) === null || _a === void 0 ? void 0 : _a[0];
                    const currentAlarmStatus = (currentPoint === null || currentPoint === void 0 ? void 0 : currentPoint.PointStatus) || (currentPoint === null || currentPoint === void 0 ? void 0 : currentPoint.AlarmStatus) || 0;
                    const initialSample = (0, mergeMenuSignaling_1.createMenuAlarmSample)(currentAlarmStatus, (_b = train.ldd) !== null && _b !== void 0 ? _b : undefined, dbTz);
                    history = initialSample ? [initialSample] : [];
                }
                for (const update of trainUpdates) {
                    const sample = (0, mergeMenuSignaling_1.createMenuAlarmSample)(update.alarmStatus, update.date, dbTz);
                    if (sample) {
                        history.push(sample);
                    }
                }
                let referenceTimestamp = (0, utils_1.toFakeUtc)(new Date(), dbTz).getTime();
                for (const sample of history) {
                    referenceTimestamp = Math.max(referenceTimestamp, sample.timestamp);
                }
                const windowStart = (0, utils_1.calculateStartDate)(new Date(referenceTimestamp), provider_user_1.SelfRecorderRangeEnum[recorderRange], recorderValue).getTime();
                const resolved = (0, mergeMenuSignaling_1.resolveWorstMenuAlarmSample)(history, windowStart);
                trainAlarmHistoryRef.current.set(train.TrainId, resolved.samples);
                const latestUpdate = trainUpdates[trainUpdates.length - 1];
                const alarmStatus = (_d = (_c = resolved.worst) === null || _c === void 0 ? void 0 : _c.alarmStatus) !== null && _d !== void 0 ? _d : latestUpdate.alarmStatus;
                const alarmDate = (_f = (_e = resolved.worst) === null || _e === void 0 ? void 0 : _e.date) !== null && _f !== void 0 ? _f : latestUpdate.date;
                stationChanged = true;
                const currentPoint = (_h = (_g = train.Points) === null || _g === void 0 ? void 0 : _g[0]) !== null && _h !== void 0 ? _h : {};
                return Object.assign(Object.assign({}, train), { ldd: alarmDate !== null && alarmDate !== void 0 ? alarmDate : train.ldd, Points: [
                        Object.assign(Object.assign({}, currentPoint), { AlarmStatus: alarmStatus, PointStatus: alarmStatus }),
                        ...((_k = (_j = train.Points) === null || _j === void 0 ? void 0 : _j.slice(1)) !== null && _k !== void 0 ? _k : []),
                    ] });
            });
            return stationChanged ? Object.assign(Object.assign({}, station), { Trains: nextTrains }) : station;
        });
        for (const [stationId, stationUpdates] of updatesByStation) {
            const history = (_d = stationAlarmHistoryRef.current.get(stationId)) !== null && _d !== void 0 ? _d : [];
            for (const update of stationUpdates) {
                const sample = (0, mergeMenuSignaling_1.createMenuAlarmSample)(update.alarmStatus, update.date, dbTz);
                if (sample) {
                    history.push(sample);
                }
            }
            let referenceTimestamp = (0, utils_1.toFakeUtc)(new Date(), dbTz).getTime();
            for (const sample of history) {
                referenceTimestamp = Math.max(referenceTimestamp, sample.timestamp);
            }
            const windowStart = (0, utils_1.calculateStartDate)(new Date(referenceTimestamp), provider_user_1.SelfRecorderRangeEnum[recorderRange], recorderValue).getTime();
            const resolved = (0, mergeMenuSignaling_1.resolveWorstMenuAlarmSample)(history, windowStart);
            stationAlarmHistoryRef.current.set(stationId, resolved.samples);
            const latestUpdate = stationUpdates[stationUpdates.length - 1];
            stationStatusOverridesRef.current.set(stationId, (_f = (_e = resolved.worst) === null || _e === void 0 ? void 0 : _e.alarmStatus) !== null && _f !== void 0 ? _f : latestUpdate.alarmStatus);
        }
        const nextMenuList = new menu_list_1.MenuList(nextRawList);
        const nextLookup = (0, menu_lookup_1.buildMenuLookup)(nextRawList);
        const filtered = getFilteredMenuList(nextMenuList, filters, currentStatusFilter);
        setMenuData({
            rawList: nextRawList,
            menuList: nextMenuList,
            lookup: nextLookup,
        });
        setList(filtered);
    }, [appliedRecorderModeSettings, currentStatusFilter, dbTz, filters, getFilteredMenuList, selfRecorderSettings]);
    const updateListDebounced = (0, use_debounce_1.useDebouncedCallback)(async ({ query: _query, startTimestamp, endTimestamp, equipmentStateDisplayMode: equipmentStateDisplayModeValue, signalingOnly, }) => {
        const requestVersion = updateListRequestVersionRef.current;
        const actualStartTimestamp = startTimestamp;
        const actualEndTimestamp = endTimestamp;
        if (userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE) {
            if (!isRecorderModeActive) {
                return;
            }
        }
        const isTreeLoaded = rawList.length > 0;
        const isSignalingRefresh = !!signalingOnly && isTreeLoaded;
        const menuDataPromise = fetchMenuData(undefined, {
            startTimestamp: actualStartTimestamp,
            endTimestamp: actualEndTimestamp,
            equipmentStateDisplayMode: equipmentStateDisplayModeValue,
        });
        const treePromise = isSignalingRefresh ? Promise.resolve(undefined) : fetchMenuTree(undefined);
        const [treeJson, dataJson] = await Promise.all([treePromise, menuDataPromise]);
        if (requestVersion !== updateListRequestVersionRef.current) {
            return;
        }
        const mappedData = dataJson && Array.isArray(dataJson) ? menu_list_1.MenuList.MapMenuDataJson(dataJson) : null;
        if (isSignalingRefresh) {
            if (mappedData) {
                applySignalingUpdate(mappedData);
            }
            return;
        }
        if (!treeJson || !Array.isArray(treeJson)) {
            setIsMenuLoadFailed(true);
            setList(currentList => currentList !== null && currentList !== void 0 ? currentList : []);
            return;
        }
        setIsMenuLoadFailed(false);
        const mappedTree = menu_list_1.MenuList.MapJson(treeJson);
        if (mappedData) {
            menu_list_1.MenuList.mergeSignalingIntoRawList(mappedTree, mappedData);
        }
        stationStatusOverridesRef.current.clear();
        stationAlarmHistoryRef.current.clear();
        trainAlarmHistoryRef.current.clear();
        const nextMenuData = createMenuData(mappedTree);
        const filtered = getFilteredMenuList(nextMenuData.menuList, filters, currentStatusFilter);
        setMenuData(nextMenuData);
        setList(filtered);
    }, 300);
    const updateList = (0, react_1.useMemo)(() => {
        const runUpdateList = ((values) => {
            if (!user.isAuthenticated()) {
                return undefined;
            }
            updateListRequestVersionRef.current += 1;
            return updateListDebounced(values);
        });
        runUpdateList.cancel = updateListDebounced.cancel;
        runUpdateList.flush = updateListDebounced.flush;
        runUpdateList.isPending = updateListDebounced.isPending;
        return runUpdateList;
    }, [updateListDebounced, user.isAuthenticated()]);
    const retryMenu = () => {
        if (isLoading) {
            return;
        }
        void updateList({
            startTimestamp: isRecorderModeActive && recorderWindowStart ? recorderWindowStart : startTimestamp,
            endTimestamp: isRecorderModeActive && recorderWindowEnd ? recorderWindowEnd : endTimestamp,
            equipmentStateDisplayMode,
        });
    };
    const updateQuery = (value) => {
        const normalizedValue = (value === null || value === void 0 ? void 0 : value.trim()) || null;
        updateQS({
            query: normalizedValue,
        });
    };
    const resetSearchList = () => {
        updateQuery(null);
    };
    const resetProvider = () => {
        resetSearchList();
        stationStatusOverridesRef.current.clear();
        stationAlarmHistoryRef.current.clear();
        trainAlarmHistoryRef.current.clear();
        setMenuData(createMenuData());
        setList(null);
        setIsMenuLoadFailed(false);
        setSelectedItem(null);
        setFilters(interfaces_1.emptyFilters);
        setIsFilterApplied(false);
        setFilterOptions({});
        setCurrentStatusFilter(interfaces_1.FilterStatus.all);
        setStartTimestamp(defaultStartTimestamp);
        setEndTimestamp(defaultEndTimestamp);
    };
    const resetRecorderState = (0, react_1.useCallback)(() => {
        setRecorderTickId(0);
        setRecorderWindowStart("");
        setRecorderWindowEnd("");
        recorderLastTickTimeRef.current = null;
    }, []);
    const triggerRecorderReset = (0, react_1.useCallback)(() => {
        setRecorderResetKey(v => v + 1);
    }, []);
    const restoreRecorderModeInputsFromSettings = (0, react_1.useCallback)(() => {
        var _a;
        setRecorderModeInterval((_a = selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.value) !== null && _a !== void 0 ? _a : 0);
        setUserSelectedRecorderModePeriod(getRecorderModePeriodName(selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.range));
        setIsRecorderModeDraftDirty(false);
    }, [selfRecorderSettings]);
    const updateUserSelectedRecorderModePeriod = (0, react_1.useCallback)((value) => {
        setUserSelectedRecorderModePeriod(value);
        setIsRecorderModeDraftDirty(true);
    }, []);
    const updateRecorderModeInterval = (0, react_1.useCallback)((value) => {
        setRecorderModeInterval(value);
        setIsRecorderModeDraftDirty(true);
    }, []);
    const stopRecorderMode = (0, react_1.useCallback)(() => {
        setAppliedRecorderModeSettings(null);
        resetRecorderState();
    }, [resetRecorderState]);
    const resetTimeRange = (0, react_1.useCallback)(() => {
        const { start, end } = getPresetRangeTimestamps(interfaces_1.RangeType.WORK_DAY, (0, utils_1.toFakeUtc)(new Date(), dbTz));
        stopRecorderMode();
        setUserSelectedRangeType(interfaces_1.RangeType.WORK_DAY);
        setManualRangeType(null);
        setStartTimestamp(start);
        setEndTimestamp(end);
        setUserSelectedStartTimestamp(new Date(start));
        setUserSelectedEndTimestamp(new Date(end));
        setIsJournalNavigation(false);
        setJournalTimeRangeHistory([]);
        setJustRestoredRange(false);
    }, [dbTz, stopRecorderMode]);
    const startRecorderMode = (0, react_1.useCallback)((settings) => {
        resetRecorderState();
        setAppliedRecorderModeSettings(settings);
        setIsRecorderModeDraftDirty(false);
        triggerRecorderReset();
    }, [resetRecorderState, triggerRecorderReset]);
    const startRecorderModeFromSettings = (0, react_1.useCallback)(() => {
        const recorderModeSettings = getRecorderModeApplySettings(selfRecorderSettings);
        if (!recorderModeSettings) {
            return;
        }
        restoreRecorderModeInputsFromSettings();
        startRecorderMode(recorderModeSettings);
    }, [restoreRecorderModeInputsFromSettings, selfRecorderSettings, startRecorderMode]);
    const updateStartTimestamp = (value) => {
        setStartTimestamp((0, utils_1.toFakeUtcISO)(value, dbTz));
    };
    const updateEndTimestamp = (value) => {
        setEndTimestamp((0, utils_1.toFakeUtcISO)(value, dbTz));
    };
    const updateTimestamps = (start, end) => {
        updateStartTimestamp(start);
        updateEndTimestamp(end);
    };
    const updateTimestampsFakeUTC = (start, end) => {
        const endMs = new Date(end).getTime();
        let startMs = new Date(start).getTime();
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) {
            startMs = endMs - 1;
        }
        setStartTimestamp(new Date(startMs).toISOString());
        setEndTimestamp(end);
    };
    const updateTab = (value) => {
        updateQS({
            tab: value,
        });
        settings.setTab(value);
    };
    const updatePrecision = (value) => {
        setPrecision(value);
        updateQS({
            precision: value,
        });
    };
    const getParentsNames = (parentId) => {
        return [...(menuLookup.parentNamesByStationId.get(parentId) || [])];
    };
    const getParentsIds = (parentId) => {
        return [...(menuLookup.parentIdsByStationId.get(parentId) || [])];
    };
    const getTrain = (id) => {
        const train = menuLookup.trainsById.get(id);
        return train
            ? Object.assign(Object.assign({}, train), { ParentsNames: [...train.ParentsNames] }) : null;
    };
    const getTrainAlarmStatus = (trainId) => {
        var _a;
        const foundTrain = getTrain(trainId);
        const point = (_a = foundTrain === null || foundTrain === void 0 ? void 0 : foundTrain.Points) === null || _a === void 0 ? void 0 : _a[0];
        return (point === null || point === void 0 ? void 0 : point.PointStatus) || (point === null || point === void 0 ? void 0 : point.AlarmStatus) || (isVisualControlOpen && (point === null || point === void 0 ? void 0 : point.VisualControlAlarmStatus)) || 0;
    };
    const selectStation = (id, options) => {
        const foundItem = menuLookup.stationsById.get(id) || null;
        const isSameSelection = selectedItem && "Id" in selectedItem && selectedItem.Id === id;
        if (userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE && !(options === null || options === void 0 ? void 0 : options.keepRecorderState) && !isSameSelection) {
            resetRecorderState();
        }
        setSelectedItem(foundItem);
        settings.setStationId(`${id}`);
    };
    const selectTrain = (id, options) => {
        const train = getTrain(id);
        const isSameSelection = selectedItem && "TrainId" in selectedItem && selectedItem.TrainId != null && selectedItem.TrainId === id;
        if (userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE && !(options === null || options === void 0 ? void 0 : options.keepRecorderState) && !isSameSelection) {
            resetRecorderState();
        }
        setSelectedItem(train);
        settings.setTrainId(`${id}`);
    };
    const getListedStation = (list, stationId) => {
        let station = null;
        if (!list) {
            return null;
        }
        for (const item of list) {
            if (station) {
                return station;
            }
            station = item.Id === stationId ? item : getListedStation(item.children, stationId);
        }
        return station;
    };
    const getStationAlarmStatus = (stationId) => {
        const foundStation = getListedStation(list, stationId);
        return (foundStation === null || foundStation === void 0 ? void 0 : foundStation.MaxAlarmStatus) || 0;
    };
    const searchIndex = (0, react_1.useMemo)(() => (0, search_list_1.buildMenuSearchIndex)(list), [list]);
    const searchList = (0, react_1.useMemo)(() => (0, search_list_1.searchMenuIndex)(searchIndex, query), [searchIndex, query]);
    const getAllNestedTrains = (stationId) => {
        const allNestedTrains = {};
        const station = getListedStation(list, stationId);
        let orderNumber = 0;
        const getNestedTrains = (station) => {
            return [
                ...station.Trains.filter(({ TrainId }) => TrainId),
                ...(station.children ? station.children.map(getNestedTrains).flat(2) : []),
            ];
        };
        if (station) {
            const nestedTrains = getNestedTrains(station);
            nestedTrains.forEach(item => {
                var _a, _b;
                if (item === null || item === void 0 ? void 0 : item.TrainId) {
                    const train = getTrain(item.TrainId);
                    const point = (_b = ((_a = train === null || train === void 0 ? void 0 : train.Points) !== null && _a !== void 0 ? _a : item.Points)) === null || _b === void 0 ? void 0 : _b[0];
                    allNestedTrains[item.TrainId] = {
                        name: train === null || train === void 0 ? void 0 : train.TrainName,
                        trainColour: train === null || train === void 0 ? void 0 : train.TrainColour,
                        parentId: train === null || train === void 0 ? void 0 : train.ParentId,
                        parentsNames: `${(train === null || train === void 0 ? void 0 : train.ParentsNames[(train === null || train === void 0 ? void 0 : train.ParentsNames.length) - 1]) || ""} - ${(train === null || train === void 0 ? void 0 : train.ParentsNames.join("/")) || ""}`,
                        measures: 0,
                        alarmStatus: (point === null || point === void 0 ? void 0 : point.PointStatus) || (point === null || point === void 0 ? void 0 : point.AlarmStatus) || (isVisualControlOpen && (point === null || point === void 0 ? void 0 : point.VisualControlAlarmStatus)) || 0,
                        orderNumber,
                        userSortNum: typeof (train === null || train === void 0 ? void 0 : train.UserSortNum) === "number" && !Number.isNaN(train.UserSortNum)
                            ? train.UserSortNum
                            : orderNumber,
                    };
                    orderNumber += 1;
                }
            });
        }
        return allNestedTrains;
    };
    const getTrainsByFilter = (filters) => {
        const newList = getFilteredMenuList(rawMenuList, filters, currentStatusFilter);
        setList(newList);
        if (newList[0]) {
            setSelectedItem(newList[0]);
        }
    };
    const updateFilters = (filters) => {
        setIsFilterApplied(true);
        setFilters(filters);
    };
    const resetFilters = (0, react_1.useCallback)(() => {
        setIsFilterApplied(false);
        setFilters(interfaces_1.emptyFilters);
    }, []);
    const showAllStatusFilter = () => {
        setCurrentStatusFilter(interfaces_1.FilterStatus.all);
        setList(getFilteredMenuList(rawMenuList, filters, interfaces_1.FilterStatus.all));
    };
    const showWarningStatusFilter = () => {
        setCurrentStatusFilter(interfaces_1.FilterStatus.warning);
        setList(getFilteredMenuList(rawMenuList, filters, interfaces_1.FilterStatus.warning));
    };
    const showNonWarningStatusFilter = () => {
        setCurrentStatusFilter(interfaces_1.FilterStatus.nonWarning);
        setList(getFilteredMenuList(rawMenuList, filters, interfaces_1.FilterStatus.nonWarning));
    };
    const updateFilterOptions = (name, options) => {
        const newFilterOptions = Object.assign({}, filterOptions);
        newFilterOptions[name] = options;
        setFilterOptions(newFilterOptions);
    };
    const shouldShowBasedOnInterval = () => {
        const last = settings.getPasswordExpireyLastShownAt();
        if (!last)
            return true;
        const lastTime = new Date(last);
        const now = new Date();
        const diffHours = (now.getTime() - lastTime.getTime()) / (1000 * 60 * 60);
        return diffHours >= interfaces_1.NOTIFY_INTERVAL_HOURS;
    };
    const markNotificationShown = () => {
        settings.setPasswordExpiryLastShownAt(new Date().toISOString());
    };
    const getPasswordExpiryNotification = (passwordExpiresAt, daysBefore = 3) => {
        if (!passwordExpiresAt) {
            return null;
        }
        if (!(0, utils_1.isPasswordExpiringSoon)(passwordExpiresAt, daysBefore)) {
            return null;
        }
        if (!shouldShowBasedOnInterval()) {
            return null;
        }
        markNotificationShown();
        return "passwordExpiresSoon";
    };
    const changeUserSelectedRangeType = (value) => {
        if (value !== userSelectedRangeType &&
            (value === interfaces_1.RangeType.RECORDER_MODE || userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE)) {
            stopRecorderMode();
            if (value === interfaces_1.RangeType.RECORDER_MODE) {
                startRecorderModeFromSettings();
            }
        }
        setUserSelectedRangeType(value);
        settings.setRangeType(value);
    };
    const changeTablesActiveTab = (newValue) => {
        setTablesActiveTab(newValue);
    };
    const restoreSessionFromUserSettings = (0, react_1.useCallback)((userId) => {
        var _a, _b;
        const userSettings = readUserSettingsFromLocalStorage(userId);
        const restoredRangeType = userSettings.rangeType || interfaces_1.RangeType.WORK_DAY;
        const tabForRestore = userSettings.tab || "1";
        const isRecorderModeDisabledForRestoreTab = disabledRecorderModeTabs.includes(tabForRestore);
        const effectiveRangeType = isRecorderModeDisabledForRestoreTab && restoredRangeType === interfaces_1.RangeType.RECORDER_MODE
            ? interfaces_1.RangeType.WORK_DAY
            : restoredRangeType;
        const isRecorder = effectiveRangeType === interfaces_1.RangeType.RECORDER_MODE;
        const savedStart = userSettings.startTimestamp;
        const savedEnd = userSettings.endTimestamp;
        let nextStart;
        let nextEnd;
        if (isRecorder) {
            nextEnd = nowFakeUtc.toISOString();
            nextStart = (0, utils_1.calculateStartDate)(nowFakeUtc, provider_user_1.SelfRecorderRangeEnum[(_a = selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.range) !== null && _a !== void 0 ? _a : provider_user_1.SelfRecorderRangeEnum.minutes], (_b = selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.value) !== null && _b !== void 0 ? _b : 5).toISOString();
        }
        else if (savedStart && savedEnd) {
            nextStart = savedStart;
            nextEnd = savedEnd;
            setJustRestoredRange(true);
        }
        else {
            const presetRange = getPresetRangeTimestamps(effectiveRangeType, nowFakeUtc);
            nextStart = presetRange.start;
            nextEnd = presetRange.end;
        }
        setUserSelectedRangeType(effectiveRangeType);
        setStartTimestamp(nextStart);
        setEndTimestamp(nextEnd);
        setUserSelectedStartTimestamp(new Date(nextStart));
        setUserSelectedEndTimestamp(new Date(nextEnd));
        setManualRangeType(null);
        setMenuData(createMenuData());
        setList(null);
        setIsMenuLoadFailed(false);
        setSelectedItem(null);
        setFilters(interfaces_1.emptyFilters);
        setIsFilterApplied(false);
        setFilterOptions({});
        setCurrentStatusFilter(interfaces_1.FilterStatus.all);
        if (effectiveRangeType === interfaces_1.RangeType.RECORDER_MODE) {
            startRecorderModeFromSettings();
        }
        const qsUpdate = {
            rangeType: effectiveRangeType,
            startTimestamp: nextStart,
            endTimestamp: nextEnd,
        };
        const urlTrainId = searchParams.get("trainId");
        const urlStationId = searchParams.get("stationId");
        if (urlTrainId) {
            qsUpdate.trainId = ~~urlTrainId;
        }
        else if (urlStationId) {
            qsUpdate.stationId = ~~urlStationId;
        }
        else if (userSettings.trainId) {
            qsUpdate.trainId = ~~userSettings.trainId;
        }
        else if (userSettings.stationId) {
            qsUpdate.stationId = ~~userSettings.stationId;
        }
        if (tabForRestore) {
            qsUpdate.tab = tabForRestore;
        }
        updateQS(qsUpdate);
    }, [
        disabledRecorderModeTabs,
        nowFakeUtc,
        searchParams,
        selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.range,
        selfRecorderSettings === null || selfRecorderSettings === void 0 ? void 0 : selfRecorderSettings.value,
        startRecorderModeFromSettings,
        updateQS,
    ]);
    const reloadDashboardData = (0, react_1.useCallback)((userId) => {
        restoreSessionFromUserSettings(userId);
        updateListRequestVersionRef.current += 1;
    }, [restoreSessionFromUserSettings]);
    (0, react_1.useLayoutEffect)(() => {
        var _a, _b;
        const userId = user.isAuthenticated() && hasStoredUserProfile() ? (_b = (_a = user.profile) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null : null;
        if (!userId) {
            return;
        }
        if (restoredForUserIdRef.current === userId) {
            return;
        }
        restoredForUserIdRef.current = userId;
        restoreSessionFromUserSettings(userId);
    }, [restoreSessionFromUserSettings, user.isAuthenticated(), (_o = user.profile) === null || _o === void 0 ? void 0 : _o.id]);
    (0, react_1.useEffect)(() => {
        const handleUserLoggedIn = (event) => {
            var _a;
            const userId = (_a = event.detail) === null || _a === void 0 ? void 0 : _a.userId;
            if (!userId) {
                return;
            }
            restoredForUserIdRef.current = userId;
            reloadDashboardData(userId);
        };
        const handleSessionExpired = () => {
            restoredForUserIdRef.current = null;
            resetProvider();
        };
        window.addEventListener("user_logged_in", handleUserLoggedIn);
        window.addEventListener("session_expired", handleSessionExpired);
        return () => {
            window.removeEventListener("user_logged_in", handleUserLoggedIn);
            window.removeEventListener("session_expired", handleSessionExpired);
        };
    }, [reloadDashboardData]);
    (0, react_hooks_1.useDeepCompareEffect)(() => {
        if (user.isAuthenticated() && isFilterApplied && filters && rawList.length) {
            getTrainsByFilter(filters);
        }
    }, [filters, isFilterApplied, rawList, [user.isAuthenticated()]]);
    (0, react_hooks_1.useDeepCompareEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return;
        }
        const appliedFilters = Object.keys(filters).filter(key => {
            const value = filters[key];
            return !!value;
        });
        if (!isFilterApplied && !appliedFilters.length && rawList.length) {
            const newList = rawMenuList.getListWithDefaultStatuses(rawMenuList.getDefaultList(), isVisualControlOpen);
            setList(newList);
        }
    }, [filters, isFilterApplied, rawList, [user.isAuthenticated()]]);
    (0, react_hooks_1.useDeepCompareEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return;
        }
        if (rawList.length) {
            switch (currentStatusFilter) {
                case interfaces_1.FilterStatus.warning:
                    return showWarningStatusFilter();
                case interfaces_1.FilterStatus.nonWarning:
                    return showNonWarningStatusFilter();
                case interfaces_1.FilterStatus.all:
                default:
                    return showAllStatusFilter();
            }
        }
    }, [rawList]);
    (0, react_hooks_1.useDeepCompareEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return;
        }
        if (!selectedItem && (list === null || list === void 0 ? void 0 : list.length)) {
            const stationId = searchParams.get("stationId") || savedStationId;
            const trainId = searchParams.get("trainId") || savedTrainId;
            if (trainId) {
                selectTrain(~~trainId, { keepRecorderState: true });
            }
            else if (stationId) {
                selectStation(~~stationId, { keepRecorderState: true });
            }
            else {
                setSelectedItem(list[0]);
            }
        }
    }, [selectedItem, list, [location.search, user.isAuthenticated()]]);
    (0, react_hooks_1.useDeepCompareEffect)(() => {
        if (!user.isAuthenticated() || !selectedItem || !hasStoredUserProfile()) {
            return;
        }
        const isStation = "Id" in selectedItem;
        const updateData = { precision, tab, query };
        if (isStation) {
            updateData.stationId = selectedItem.Id;
            updateData.trainId = null;
            settings.setStationId(`${selectedItem.Id}`);
            settings.setTrainId("");
        }
        else {
            updateData.trainId = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.TrainId;
            updateData.stationId = null;
            settings.setTrainId(`${(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.TrainId) || ""}`);
            settings.setStationId("");
        }
        if (!isJournalNavigation) {
            updateData.startTimestamp = startTimestamp;
            updateData.endTimestamp = endTimestamp;
            updateData.rangeType = userSelectedRangeType;
        }
        updateQS(updateData);
    }, [
        selectedItem,
        [user.isAuthenticated()],
        { endTimestamp, startTimestamp, userSelectedRangeType, precision, tab, query },
        isJournalNavigation,
    ]);
    (0, react_1.useEffect)(() => {
        if (userSelectedRangeType !== interfaces_1.RangeType.RECORDER_MODE ||
            !selfRecorderSettings ||
            !appliedRecorderModeSettings ||
            !selectedItemKey) {
            return;
        }
        const loopSettings = Object.assign(Object.assign(Object.assign({}, selfRecorderSettings), appliedRecorderModeSettings), { dbTz });
        const stopLoop = (0, recorder_loop_1.startRecorderLoop)(loopSettings, {
            onTick: ({ tickId, windowStart, windowEnd, deltaStart, deltaEnd }) => {
                setRecorderTickId(tickId);
                updateTimestampsFakeUTC(deltaStart, deltaEnd);
                setRecorderWindowStart(windowStart);
                setRecorderWindowEnd(windowEnd);
            },
        }, recorderLastTickTimeRef);
        return () => {
            stopLoop();
        };
    }, [
        selfRecorderSettings,
        appliedRecorderModeSettings,
        userSelectedRangeType,
        dbTz,
        selectedItemKey,
        recorderResetKey,
    ]);
    (0, react_1.useEffect)(() => {
        if (userSelectedRangeType !== interfaces_1.RangeType.RECORDER_MODE) {
            setIsRecorderModeDraftDirty(false);
            return;
        }
        if (isRecorderModeActive || isRecorderModeDraftDirty) {
            return;
        }
        startRecorderModeFromSettings();
    }, [isRecorderModeActive, isRecorderModeDraftDirty, startRecorderModeFromSettings, userSelectedRangeType]);
    (0, react_1.useEffect)(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible") {
                return;
            }
            if (userSelectedRangeType !== interfaces_1.RangeType.RECORDER_MODE || !appliedRecorderModeSettings) {
                return;
            }
            triggerRecorderReset();
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [userSelectedRangeType, appliedRecorderModeSettings, triggerRecorderReset]);
    (0, react_1.useEffect)(() => {
        if (!user.isAuthenticated() || !startTimestamp || !endTimestamp || !hasStoredUserProfile()) {
            return;
        }
        if (userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE && isRecorderModeActive && !recorderWindowStart) {
            return;
        }
        if (subscribeSignalingActive && rawList.length > 0) {
            return;
        }
        void updateList({
            startTimestamp,
            endTimestamp,
            equipmentStateDisplayMode,
            signalingOnly: rawList.length > 0,
        });
    }, [startTimestamp, endTimestamp, subscribeSignalingActive]);
    (0, react_1.useEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return;
        }
        if (userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE) {
            if (!isRecorderModeActive || !recorderModeInterval || !userSelectedRecorderModePeriod)
                return;
        }
        if (list === null && !isLoading) {
            void updateList({
                startTimestamp,
                endTimestamp,
                equipmentStateDisplayMode,
            });
        }
    }, [
        list,
        isLoading,
        user.isAuthenticated(),
        userSelectedRangeType,
        isRecorderModeActive,
        recorderModeInterval,
        userSelectedRecorderModePeriod,
    ]);
    (0, react_1.useEffect)(() => {
        if (!user.isAuthenticated() ||
            !startTimestamp ||
            !endTimestamp ||
            !shouldReloadJournal ||
            !hasStoredUserProfile()) {
            return;
        }
        void updateList({
            startTimestamp,
            endTimestamp,
            equipmentStateDisplayMode,
            signalingOnly: rawList.length > 0,
        });
        setShouldReloadJournal(false);
    }, [shouldReloadJournal]);
    (0, react_1.useEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return;
        }
        void updateList({
            startTimestamp,
            endTimestamp,
            equipmentStateDisplayMode,
            signalingOnly: rawList.length > 0,
        });
    }, [isVisualControlOpen]);
    (0, react_1.useEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            return;
        }
        void updateList({
            startTimestamp,
            endTimestamp,
            equipmentStateDisplayMode,
            signalingOnly: rawList.length > 0,
        });
    }, [equipmentStateDisplayMode]);
    (0, react_1.useEffect)(() => {
        if (!user.isAuthenticated() || !hasStoredUserProfile()) {
            restoredForUserIdRef.current = null;
            resetProvider();
        }
    }, [user.isAuthenticated()]);
    (0, react_1.useEffect)(() => {
        if (isRecorderModeDisabledForTab && userSelectedRangeType === interfaces_1.RangeType.RECORDER_MODE) {
            stopRecorderMode();
            setUserSelectedRangeType(interfaces_1.RangeType.WORK_DAY);
            updateQS({ rangeType: interfaces_1.RangeType.WORK_DAY });
        }
    }, [tab, stopRecorderMode, userSelectedRangeType]);
    return ((0, jsx_runtime_1.jsx)(context_1.AsuContext.Provider, { value: {
            updateQuery,
            updateStartTimestamp,
            updateEndTimestamp,
            updateTimestamps,
            updateTimestampsFakeUTC,
            userSelectedStartTimestamp,
            userSelectedEndTimestamp,
            userSelectedRangeType,
            changeUserSelectedRangeType,
            userSelectedRecorderModePeriod,
            setUserSelectedRecorderModePeriod: updateUserSelectedRecorderModePeriod,
            recorderModeInterval,
            setRecorderModeInterval: updateRecorderModeInterval,
            updatingValueRecorderMode,
            setUpdatingValueRecorderMode,
            setUserSelectedStartTimestamp,
            setUserSelectedEndTimestamp,
            recorderTickId,
            recorderWindowStart,
            recorderWindowEnd,
            resetRecorderState,
            triggerRecorderReset,
            isRecorderModeActive,
            isRecorderModeDraftDirty,
            startRecorderMode,
            stopRecorderMode,
            resetTimeRange,
            updateList,
            retryMenu,
            isMenuLoadFailed,
            setSubscribeSignalingActive,
            applySubscribeMenuStatusUpdates,
            resetSearchList,
            updateQS,
            dbTz,
            setDbTz,
            isMenuOpen,
            setIsMenuOpen,
            isAmplitudePhaseSelected,
            setIsAmplitudePhaseSelected,
            isVisualControlOpen,
            setIsVisualControlOpen,
            tab,
            updateTab,
            tablesActiveTab,
            changeTablesActiveTab,
            precision,
            updatePrecision,
            selectedItem,
            selectStation,
            selectTrain,
            getParentsIds,
            getParentsNames,
            getTrain,
            getTrainAlarmStatus,
            getStationAlarmStatus,
            getAllNestedTrains,
            showAllStatusFilter,
            showWarningStatusFilter,
            showNonWarningStatusFilter,
            currentStatusFilter,
            setCurrentStatusFilter,
            updateFilters,
            resetFilters,
            filters,
            isFilterApplied,
            filterOptions,
            updateFilterOptions,
            query,
            startTimestamp,
            endTimestamp,
            rangeType,
            isLoading,
            list,
            searchList,
            shouldReloadJournal,
            setShouldReloadJournal,
            isJournalNavigation,
            setIsJournalNavigation,
            manualRangeType,
            setManualRangeType,
            journalTimeRangeHistory,
            setJournalTimeRangeHistory,
            skipNextJournalReload,
            setSkipNextJournalReload,
            justRestoredRange,
            setJustRestoredRange,
            shouldShowBasedOnInterval,
            markNotificationShown,
            getPasswordExpiryNotification,
            pendingAnalyticsParams,
            setPendingAnalyticsParams,
        }, children: children }));
};
exports.AsuProvider = AsuProvider;
//# sourceMappingURL=provider.js.map